<template>
  <InputWrapper
    v-slot="{ inputId }"
    :input-id="id"
    :label="t('orders.cancellationReason')"
    class="order-cancellation-reason-filter w-2"
    optional
  >
    <RygenDropdown
      :id="inputId"
      v-model="internalValue.cancellationReason"
      :options="cancellationReasons.data"
      :loading="cancellationReasons.loading"
      :placeholder="t('orders.cancellationReason')"
      append-to="body"
      option-label="name"
      option-value="name"
      show-clear
    />
  </InputWrapper>
</template>

<script lang="ts" setup>
import axios from 'axios';
import type { Ref } from 'vue';
import { onMounted, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useModelValue, useToast } from '@rygen/rygen-ui/composables';
import { Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import { CancellationReason, OrderSearchCriteria } from '@/model';

import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: OrderSearchCriteria,
    default: () => new OrderSearchCriteria(),
  },
});
const { id, modelValue } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: OrderSearchCriteria): void;
}>();
const { t } = useI18n();
const toast = useToast();

const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts: Partial<OrderSearchCriteria>) =>
    new OrderSearchCriteria(opts),
});

const cancellationReasons: Ref<Loadable<CancellationReason[]>> = ref({
  loading: false,
});

const loadCancellationReasons = async () => {
  try {
    if (cancellationReasons.value.data) {
      return;
    }

    cancellationReasons.value.loading = true;
    cancellationReasons.value.data =
      await api.orderCancellationReason.findAll();
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    cancellationReasons.value.error = e;
    console.error('Error loading cancellation reasons', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('orders.cancellationReason'),
      }),
      error: e,
    });
  } finally {
    cancellationReasons.value.loading = false;
  }
};

onMounted(() => {
  loadCancellationReasons();
});
</script>
